/**
 * AP-09-E: Einheitlicher Datenzugriff für die Buchungsmasken.
 *
 * Die Buchungsmaske (ClockPage, SummaryPage) muss – abhängig vom
 * DataSourceMode pro Stammdatenbereich – entweder die Legacy-Stammdaten
 * (/time-config/projects | /time-config/activity-types) oder den neuen
 * Ref-Layer (/time-tracking/picklists/*) befüllen.
 *
 * Dieser Hook kapselt die Entscheidungslogik:
 *   - liest `projectSource`, `activityTypeSource`, `userSource` aus der
 *     Tenant-Konfiguration. WICHTIG: über `getClockSettings()`
 *     (/time-config/clock-settings) und NICHT über `get()` (/time-config) —
 *     letzterer ist ADMIN-only. Jede Buchungsrolle unter ADMIN bekam sonst
 *     einen 403, `mode` blieb null und die Maske fiel auf die Legacy-Quellen
 *     zurück; bei einem Tenant mit EXTERNAL_API/INTERNAL sind die leer, also
 *     waren alle Dropdowns leer.
 *   - liefert eine vereinheitlichte Struktur mit Projekten/Positionen/Steps
 *     und Tätigkeiten aus der jeweils aktiven Quelle.
 *   - liefert zusätzlich `buildBookingIds(...)`, das den passenden Payload
 *     (projectId vs. projectRefId etc.) zusammenbaut.
 *
 * Implementierung bewusst ohne @tanstack/react-query — das Frontend hat
 * diese Dependency nicht, analog zum PWA-Pendant (apps/pwa/.../
 * useBookingDataSource). Stattdessen plain useState + useEffect mit einem
 * Minimal-Cache.
 *
 * Die Entscheidungsregel:
 *   - projectSource === 'EXTERNAL_API' → Ref-Layer (Sync-gepflegte Refs).
 *   - projectSource === 'INTERNAL'     → Ref-Layer (intern via AP-13 gepflegt).
 *   - projectSource === 'MANUAL'       → Legacy / Freitext.
 *   - projectSource === 'PROJECT_MGMT' → Ref-Layer (AP-35: Spiegel des
 *                                        Projektmanagements, sonst wie EXTERNAL_API).
 *   - activityTypeSource analog (eigenständig).
 *   - userSource analog (eigenständig).
 *
 * Hintergrund: Bis zur AP-13-Auslieferung war INTERNAL ein No-Op-Default,
 * der weiter Legacy-Daten gelesen hat. Mit der CRUD-UI für Refs muss
 * INTERNAL jetzt konsequenterweise auch den Ref-Layer als Quelle nutzen,
 * sonst sind die im AP-13-UI angelegten Datensätze in der Buchungsmaske
 * unsichtbar. Legacy-Daten bestehender Tenants müssen einmalig per
 * `prisma/migrate-legacy-to-ref.ts` migriert werden.
 *
 * Siehe: docs/Arbeitspakete/AP-09/AP-09_Naechste-Schritte.md
 */
import { useEffect, useState } from 'react';
import { picklistsApi, timeConfigApi } from '../api/time-tracking.api';
import type {
  DataSourceMode,
  TimeProjectRefLite,
  TimeProjectPositionRefLite,
  TimeProjectStepRefLite,
  TimeActivityRefLite,
} from '../types/time-tracking.types';

export interface BookingDataSourceMode {
  project: DataSourceMode;
  activityType: DataSourceMode;
  user: DataSourceMode;
}

/**
 * Entscheidungsregel: Ref-Layer wird bei `EXTERNAL_API` (Sync-gepflegt)
 * UND bei `INTERNAL` (manuell über AP-13 gepflegt) aktiviert.
 * Nur `MANUAL` (Freitext) bleibt auf den Legacy-Endpoints bzw. kennt keine
 * Picklist — dort hätten Refs keine Bedeutung.
 */
export function usesRefLayer(mode: DataSourceMode): boolean {
  return mode === 'EXTERNAL_API' || mode === 'INTERNAL' || mode === 'PROJECT_MGMT';
}

export interface UseBookingDataSourceOptions {
  /**
   * Wenn true (default) werden Positionen / Steps automatisch nachgeladen.
   * `selectedProjectRefId` / `selectedPositionRefId` steuern die Kaskade.
   */
  cascadePositions?: boolean;
  selectedProjectRefId?: string | null;
  selectedPositionRefId?: string | null;
}

export interface UseBookingDataSourceResult {
  /** Aktive Modi (aus TenantTimeConfig) */
  mode: BookingDataSourceMode | null;
  /** Loading-Flag (Config noch nicht geladen) */
  isLoading: boolean;
  /** Im Ref-Modus aktive Projekte, sonst leer */
  refProjects: TimeProjectRefLite[];
  refPositions: TimeProjectPositionRefLite[];
  refSteps: TimeProjectStepRefLite[];
  refActivities: TimeActivityRefLite[];
  /**
   * Baut den richtigen ID-Payload für einen Clock-In / Create-Aufruf.
   *
   * Sendet je nach Modus entweder `projectId`/`activityTypeId` (Legacy) oder
   * `projectRefId`/`positionRefId`/`stepRefId`/`activityRefId` (Ref).
   * Die Parameter sind die jeweils ausgewählten IDs der Dropdowns — der Hook
   * weiß anhand des Modus, wo sie hingehören.
   */
  buildBookingIds: (selection: {
    legacyProjectId?: string | null;
    legacyActivityTypeId?: string | null;
    refProjectId?: string | null;
    refPositionId?: string | null;
    refStepId?: string | null;
    refActivityId?: string | null;
    refUserId?: string | null;
  }) => {
    projectId?: string;
    activityTypeId?: string;
    projectRefId?: string;
    positionRefId?: string;
    stepRefId?: string;
    activityRefId?: string;
    userRefId?: string;
  };
}

export function useBookingDataSource(
  opts: UseBookingDataSourceOptions = {},
): UseBookingDataSourceResult {
  const { cascadePositions = true, selectedProjectRefId, selectedPositionRefId } = opts;

  const [mode, setMode] = useState<BookingDataSourceMode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refProjects, setRefProjects] = useState<TimeProjectRefLite[]>([]);
  const [refPositions, setRefPositions] = useState<TimeProjectPositionRefLite[]>([]);
  const [refSteps, setRefSteps] = useState<TimeProjectStepRefLite[]>([]);
  const [refActivities, setRefActivities] = useState<TimeActivityRefLite[]>([]);

  // 1) Config laden — liefert projectSource / activityTypeSource / userSource
  useEffect(() => {
    let cancelled = false;
    timeConfigApi
      .getClockSettings()
      .then(({ data }) => {
        if (cancelled) return;
        setMode({
          project: (data.projectSource ?? 'INTERNAL') as DataSourceMode,
          activityType: (data.activityTypeSource ?? 'INTERNAL') as DataSourceMode,
          user: (data.userSource ?? 'INTERNAL') as DataSourceMode,
        });
      })
      .catch(() => {
        if (!cancelled) setMode(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const projectInRef = !!mode && usesRefLayer(mode.project);
  const activityInRef = !!mode && usesRefLayer(mode.activityType);

  // 2) Projekte (Ref) — nur im EXTERNAL_API-Modus laden
  useEffect(() => {
    if (!projectInRef) {
      setRefProjects([]);
      return;
    }
    let cancelled = false;
    picklistsApi
      .projects()
      .then(({ data }) => {
        if (!cancelled) setRefProjects(data.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setRefProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectInRef]);

  // 3) Positionen (Ref) — kaskadierend
  useEffect(() => {
    if (!projectInRef || !cascadePositions || !selectedProjectRefId) {
      setRefPositions([]);
      return;
    }
    let cancelled = false;
    picklistsApi
      .positions({ projectRefId: selectedProjectRefId })
      .then(({ data }) => {
        if (!cancelled) setRefPositions(data.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setRefPositions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectInRef, cascadePositions, selectedProjectRefId]);

  // 4) Steps (Ref) — kaskadierend
  useEffect(() => {
    if (!projectInRef || !cascadePositions || !selectedPositionRefId) {
      setRefSteps([]);
      return;
    }
    let cancelled = false;
    picklistsApi
      .steps({ positionRefId: selectedPositionRefId })
      .then(({ data }) => {
        if (!cancelled) setRefSteps(data.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setRefSteps([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectInRef, cascadePositions, selectedPositionRefId]);

  // 5) Tätigkeiten (Ref) — unabhängig
  useEffect(() => {
    if (!activityInRef) {
      setRefActivities([]);
      return;
    }
    let cancelled = false;
    picklistsApi
      .activities()
      .then(({ data }) => {
        if (!cancelled) setRefActivities(data.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setRefActivities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activityInRef]);

  const buildBookingIds: UseBookingDataSourceResult['buildBookingIds'] = (sel) => {
    if (!mode) return {};
    const out: ReturnType<UseBookingDataSourceResult['buildBookingIds']> = {};

    // Projekte / Positionen / Steps folgen `projectSource`
    if (usesRefLayer(mode.project)) {
      if (sel.refProjectId) out.projectRefId = sel.refProjectId;
      if (sel.refPositionId) out.positionRefId = sel.refPositionId;
      if (sel.refStepId) out.stepRefId = sel.refStepId;
    } else {
      if (sel.legacyProjectId) out.projectId = sel.legacyProjectId;
    }

    // Tätigkeit folgt `activityTypeSource`
    if (usesRefLayer(mode.activityType)) {
      if (sel.refActivityId) out.activityRefId = sel.refActivityId;
    } else {
      if (sel.legacyActivityTypeId) out.activityTypeId = sel.legacyActivityTypeId;
    }

    // User folgt `userSource` (meist nur PWA-Kioskmodus / Manager-Buchung)
    if (usesRefLayer(mode.user) && sel.refUserId) {
      out.userRefId = sel.refUserId;
    }

    return out;
  };

  return {
    mode,
    isLoading,
    refProjects,
    refPositions,
    refSteps,
    refActivities,
    buildBookingIds,
  };
}

/**
 * AP-09-E: SummaryPage-Helper.
 *
 * Liefert den "besten" Anzeigenamen für ein Projekt/Tätigkeit in einer
 * TimeEntry-Zeile, unabhängig davon, ob der Eintrag im Legacy- oder
 * Ref-Modus gebucht wurde. Priorität: Ref-Name > Legacy-Name > Platzhalter.
 */
export function resolveEntryProjectName(entry: {
  projectRef?: { projectNumber: string; name: string } | null;
  project?: { projectNumber: string; name: string } | null;
}): string {
  if (entry.projectRef) return `${entry.projectRef.projectNumber} – ${entry.projectRef.name}`;
  if (entry.project) return `${entry.project.projectNumber} – ${entry.project.name}`;
  return '—';
}

export function resolveEntryActivityName(entry: {
  activityRef?: { name: string } | null;
  activityType?: { name: string } | null;
}): string {
  return entry.activityRef?.name ?? entry.activityType?.name ?? '—';
}

export function resolveEntryPositionName(entry: {
  positionRef?: { nummer: number | null; name: string } | null;
}): string {
  if (!entry.positionRef) return '—';
  const n = entry.positionRef.nummer;
  return n != null ? `${n} – ${entry.positionRef.name}` : entry.positionRef.name;
}

export function resolveEntryStepName(entry: {
  stepRef?: { nummer: number | null; name: string } | null;
}): string {
  if (!entry.stepRef) return '—';
  const n = entry.stepRef.nummer;
  return n != null ? `${n} – ${entry.stepRef.name}` : entry.stepRef.name;
}
